import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ArrowLeft, Check, Copy, Loader2, QrCode, Sparkles } from "lucide-react";
import { toast } from "sonner";

const PAID_PLANS = ["starter", "pro", "scale"];

export default function PlanCheckoutPage() {
  const [searchParams] = useSearchParams();
  const planFromUrl = (searchParams.get("plan") || "").trim().toLowerCase();

  const [payerCpf, setPayerCpf] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkout, setCheckout] = useState(null);
  const [err, setErr] = useState("");

  const planId = PAID_PLANS.includes(planFromUrl) ? planFromUrl : "";

  const title = useMemo(() => {
    if (!planId) return "Plano";
    const labels = { starter: "Starter", pro: "Pro", scale: "Scale" };
    return labels[planId] || planId;
  }, [planId]);

  async function createPix() {
    if (!planId) return;
    setLoading(true);
    setErr("");
    setCheckout(null);
    const digits = payerCpf.replace(/\D/g, "");
    const body = { plan_id: planId };
    if (digits.length === 11) {
      body.payer_cpf = digits;
    }
    try {
      const { data } = await api.post("/billing/mp/pix-checkout", body);
      setCheckout(data);
      toast.success("PIX gerado — pague com o app do seu banco.");
    } catch (e) {
      const raw = e.response?.data?.detail;
      const msg = formatApiError(raw) || "Não foi possível gerar o PIX.";
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  const copyPix = () => {
    const code = checkout?.qr_code;
    if (!code) return;
    navigator.clipboard.writeText(code).then(
      () => toast.success("Código PIX copiado"),
      () => toast.error("Não foi possível copiar")
    );
  };

  if (!planId) {
    return (
      <div className="max-w-lg mx-auto space-y-6">
        <Link
          to="/#precos"
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-cp-primary-light transition"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar aos preços
        </Link>
        <div className="rounded-cp-card border border-white/10 bg-cp-card/40 p-8 text-center">
          <p className="text-slate-300">Selecione um plano na página inicial para assinar.</p>
          <Link
            to="/#precos"
            className="mt-6 inline-flex items-center justify-center gap-2 bg-cp-primary hover:bg-cp-primary-hover text-cp-bg font-bold px-6 py-3 rounded-xl transition"
          >
            Ver planos
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto space-y-8">
      <div>
        <Link
          to="/app/dashboard"
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-cp-primary-light transition mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Ir ao dashboard
        </Link>
        <div className="font-mono text-xs uppercase tracking-widest text-cp-primary-light mb-2">_assinatura</div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tighter text-white flex items-center gap-2">
          <Sparkles className="h-7 w-7 text-cp-primary-light shrink-0" />
          Plano {title}
        </h1>
        <p className="text-slate-400 text-sm mt-2">
          Pague com PIX. Em produção o Mercado Pago costuma exigir o <strong className="text-slate-300">CPF de quem
          paga</strong>. Após a confirmação, o plano ativa sozinho (1–2 min).
        </p>
      </div>

      {!checkout && (
        <div className="rounded-cp-card border border-white/10 bg-cp-card/40 p-6 space-y-4">
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">
              CPF de quem vai pagar o PIX (recomendado)
            </label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder="000.000.000-00"
              value={payerCpf}
              onChange={(e) => setPayerCpf(e.target.value)}
              className="w-full bg-cp-elevated border border-white/10 focus:border-cp-primary rounded-lg px-4 py-3 text-sm text-slate-100"
            />
            <p className="text-xs text-slate-500 mt-1">11 dígitos. Pode tentar sem CPF, mas o MP pode recusar.</p>
          </div>
          <button
            type="button"
            disabled={loading}
            onClick={() => void createPix()}
            className="w-full bg-cp-primary hover:bg-cp-primary-hover disabled:opacity-60 text-cp-bg font-bold py-3 rounded-xl transition flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Gerando PIX…
              </>
            ) : (
              "Gerar cobrança PIX"
            )}
          </button>
        </div>
      )}

      {err && (
        <div className="rounded-cp-card border border-red-500/30 bg-red-500/10 text-red-200 text-sm px-4 py-3 whitespace-pre-wrap">
          {err}
        </div>
      )}

      {checkout && (
        <div className="space-y-6 rounded-cp-card border border-white/10 bg-cp-card/40 p-6 md:p-8">
          {checkout.payment_status && checkout.payment_status !== "approved" && (
            <div className="text-xs font-mono text-amber-200/90 bg-amber-500/10 border border-amber-500/25 rounded-lg px-3 py-2">
              Estado no MP: {checkout.payment_status} — use o QR ou o código abaixo para pagar.
            </div>
          )}

          {checkout.qr_code_base64 && (
            <div className="flex flex-col items-center gap-2">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-mono flex items-center gap-2">
                <QrCode className="h-4 w-4" />
                QR Code PIX
              </div>
              <img
                src={`data:image/png;base64,${checkout.qr_code_base64}`}
                alt="QR Code PIX"
                className="w-56 h-56 md:w-64 md:h-64 rounded-xl border border-white/10 bg-white p-2"
              />
            </div>
          )}

          {checkout.qr_code && (
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-mono mb-2">Copia e cola</div>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={checkout.qr_code}
                  className="flex-1 min-w-0 bg-cp-elevated border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-slate-200"
                />
                <button
                  type="button"
                  onClick={copyPix}
                  className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cp-primary hover:bg-cp-primary-hover text-cp-bg font-semibold text-sm"
                >
                  <Copy className="h-4 w-4" />
                  Copiar
                </button>
              </div>
            </div>
          )}

          {checkout.ticket_url && (
            <a
              href={checkout.ticket_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex text-sm text-cp-primary-light hover:underline"
            >
              Abrir comprovante / página do Mercado Pago
            </a>
          )}

          <div className="flex flex-wrap gap-3 pt-2">
            <button
              type="button"
              disabled={loading}
              onClick={() => void createPix()}
              className="text-sm text-slate-400 hover:text-white underline disabled:opacity-50"
            >
              Gerar novo PIX
            </button>
            <Link
              to="/app/dashboard"
              className="inline-flex items-center gap-2 text-sm font-semibold text-cp-primary-light hover:text-cp-primary-light"
            >
              <Check className="h-4 w-4" />
              Já paguei — ir ao dashboard
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
